import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { createPortal } from 'react-dom'

import { getTransitionPath } from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import NavBarStore from './NavBar.store'

import './NavBar.scss'

interface ResourceItem {
  label: string
  route: string
  testId?: string
}

interface ResourcesDropdownProps {
  navBarStore: NavBarStore
  items: ResourceItem[]
  className?: string
  isInOverflow?: boolean
  testId?: string
}

type DropDownPosition = { top: number; left: number; width: number }

const SHIFT_UP_PX = 4

@observer
export default class ResourcesDropdown extends React.Component<ResourcesDropdownProps> {
  @observable public open: boolean = false
  @observable public dropdownPos: DropDownPosition = {
    top: 0,
    left: 0,
    width: 0,
  }

  private buttonRef = React.createRef<HTMLDivElement>()
  private dropdownRef = React.createRef<HTMLDivElement>()

  public constructor(props: ResourcesDropdownProps) {
    super(props)
    makeObservable(this)
  }

  @action.bound
  public toggleMenu() {
    if (this.open) {
      this.open = false
      return
    }

    this.open = true
    const btn = this.buttonRef.current
    if (btn) {
      const rect = btn.getBoundingClientRect()

      if (this.props.isInOverflow) {
        this.dropdownPos = {
          top: rect.top + window.scrollY,
          left: rect.right + window.scrollX,
          width: rect.width,
        }
      } else {
        this.dropdownPos = {
          top: rect.bottom + window.scrollY - SHIFT_UP_PX,
          left: rect.left + window.scrollX,
          width: rect.width,
        }
      }
    }
  }

  @action.bound
  public handleClickOutside(event: MouseEvent) {
    if (!this.open) return
    const btn = this.buttonRef.current
    const dropdown = this.dropdownRef.current
    const target = event.target as Node
    if (
      (btn && btn.contains(target)) ||
      (dropdown && dropdown.contains(target))
    ) {
      return
    }
    this.open = false
  }

  @action.bound
  public handleWindowResize() {
    if (this.open) {
      this.open = false
    }
  }

  public componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
    window.addEventListener('resize', this.handleWindowResize)
    window.addEventListener('orientationchange', this.handleWindowResize)
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
    window.removeEventListener('resize', this.handleWindowResize)
    window.removeEventListener('orientationchange', this.handleWindowResize)
  }

  private handleItemClick = (event: React.MouseEvent, route: string) => {
    event.stopPropagation()
    this.open = false
    this.props.navBarStore.displayView(route)
  }

  private isActive(): boolean {
    const { navBarStore, items } = this.props
    const { location, activeProject } = navBarStore
    const projectCode = activeProject?.code

    if (!projectCode) return false

    return items.some(item =>
      location.pathname.includes(getTransitionPath(item.route, projectCode)),
    )
  }

  private renderButtonContent(): JSX.Element {
    return (
      <>
        {Localization.translator.resources}
        <ChevronDownIcon
          size={12}
          className={classList({
            'ml7 vertical-align-middle': true,
            rotate180: this.open,
          })}
        />
      </>
    )
  }

  private renderButton(): JSX.Element {
    const { className, isInOverflow, testId } = this.props

    if (isInOverflow) {
      return (
        <div
          className={classList({
            'navbar-item text large flex-unset nowrap no-select': true,
            active: this.isActive(),
            [className]: !!className,
          })}
          onClick={this.toggleMenu}
          ref={this.buttonRef}
          data-testid={testId}
        >
          {this.renderButtonContent()}
        </div>
      )
    }

    return (
      <div
        className={classList({
          'text large flex-unset': true,
          [className]: !!className,
        })}
      >
        <div className="navbar-overflow-menu inline-block relative">
          <div
            className={classList({
              'navbar-item text large center flex-unset nowrap more-button row no-select pointer': true,
              active: this.isActive(),
            })}
            onClick={this.toggleMenu}
            ref={this.buttonRef}
            data-testid={testId}
          >
            <div className="navbar-item-wrapper">
              {this.renderButtonContent()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  public renderDropdown(): JSX.Element {
    const { items } = this.props
    return createPortal(
      <div
        className="navbar-overflow-dropdown flex-nowrap absolute bg-white brada4"
        ref={this.dropdownRef}
        style={{
          top: this.dropdownPos.top,
          left: this.dropdownPos.left,
          minWidth: this.dropdownPos.width,
        }}
      >
        {items.map((item, i) => (
          <div
            key={i}
            className="navbar-item text large flex-unset px12 nowrap navbar-overflow-item px16 pointer"
            data-testid={item.testId}
            onClick={e => this.handleItemClick(e, item.route)}
            onMouseDown={
              this.props.isInOverflow ? e => e.stopPropagation() : undefined
            }
          >
            {item.label}
          </div>
        ))}
      </div>,
      document.body,
      'resources-dropdown-portal',
    )
  }

  public render() {
    return (
      <>
        {this.renderButton()}
        {this.open && this.renderDropdown()}
      </>
    )
  }
}
